import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { tasksAPI } from '../../services/api';
import { useRole } from '../../hooks/useRole';

const PRIORITY_COLORS = {
  Critical: { bg: '#fee2e2', text: '#dc2626' },
  High:     { bg: '#ffedd5', text: '#ea580c' },
  Medium:   { bg: '#dbeafe', text: '#2563eb' },
  Low:      { bg: '#f1f5f9', text: '#64748b' },
};

const PRIORITY_ORDER = { Critical: 0, High: 1, Medium: 2, Low: 3 };

function todayStr() {
  return new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD
}

function isOverdue(task) {
  if (!task.due_date) return false;
  if (['Done', 'Cancelled'].includes(task.status)) return false;
  return new Date(task.due_date).toLocaleDateString('en-CA') < todayStr();
}

function isDueToday(task) {
  if (!task.due_date) return false;
  if (['Done', 'Cancelled'].includes(task.status)) return false;
  return new Date(task.due_date).toLocaleDateString('en-CA') === todayStr();
}

/**
 * MyTasksWidget — shown on Dashboard.
 * Fetches the current user's role-scoped tasks and shows urgent task list + stat pills.
 * For Admin/MD: also shows a mini team stats row (Total Open / Total Overdue).
 */
export default function MyTasksWidget() {
  const { isAdminOrAbove } = useRole();

  const [tasks,       setTasks]       = useState([]);
  const [teamStats,   setTeamStats]   = useState(null);  // { open, overdue }
  const [loading,     setLoading]     = useState(true);

  useEffect(() => {
    const calls = [
      tasksAPI.getMyTasks({ limit: 100 }),
      ...(isAdminOrAbove ? [tasksAPI.getStats()] : []),
    ];

    Promise.all(calls)
      .then(([myRes, statsRes]) => {
        setTasks(myRes.data?.data?.tasks || []);

        if (isAdminOrAbove && statsRes) {
          const s = statsRes.data?.data?.stats || {};
          const open = (s['To Do'] || 0) + (s['In Progress'] || 0) + (s['Review'] || 0);
          setTeamStats({ open, overdue: null }); // overdue computed below

          // Separate lighter call to estimate team overdue
          tasksAPI.getTasks({ limit: 200 })
            .then(r => {
              const all = r.data?.data?.tasks || [];
              setTeamStats(prev => ({ ...prev, overdue: all.filter(isOverdue).length }));
            })
            .catch(() => {});
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isAdminOrAbove]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived stats ────────────────────────────────────────────────────────────
  const activeTasks    = tasks.filter(t => !['Done', 'Cancelled'].includes(t.status));
  const overdueTasks   = tasks.filter(isOverdue);
  const todayTasks     = tasks.filter(isDueToday);
  const inProgressTasks = tasks.filter(t => t.status === 'In Progress');

  // Sort: overdue → due today → by priority
  const urgentTasks = [...activeTasks]
    .sort((a, b) => {
      const aRank = isOverdue(a) ? 0 : isDueToday(a) ? 1 : 2;
      const bRank = isOverdue(b) ? 0 : isDueToday(b) ? 1 : 2;
      if (aRank !== bRank) return aRank - bRank;
      return (PRIORITY_ORDER[a.priority] ?? 3) - (PRIORITY_ORDER[b.priority] ?? 3);
    })
    .slice(0, 5);

  return (
    <div style={{
      background: '#fff', borderRadius: 12,
      border: '1px solid #e2e8f0', padding: '18px 20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    }}>
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: '#1B2A6B', margin: 0 }}>✅ My Tasks</h2>
          {!loading && (
            <span style={{
              background: '#e0e7ff', color: '#3730a3',
              borderRadius: 10, padding: '1px 8px',
              fontSize: 11, fontWeight: 700,
            }}>
              {activeTasks.length}
            </span>
          )}
        </div>
        <Link to="/tasks" style={{ fontSize: 12, color: '#C9A84C', fontWeight: 600, textDecoration: 'none' }}>
          View All Tasks →
        </Link>
      </div>

      {/* ── Stat pills ──────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ background: '#fee2e2', color: '#dc2626', borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 700 }}>
          🔴 {loading ? '—' : overdueTasks.length} Overdue
        </div>
        <div style={{ background: '#ffedd5', color: '#c2410c', borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 700 }}>
          🟠 {loading ? '—' : todayTasks.length} Due Today
        </div>
        <div style={{ background: '#dbeafe', color: '#1d4ed8', borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 700 }}>
          🔵 {loading ? '—' : inProgressTasks.length} In Progress
        </div>
      </div>

      {/* ── Task list ────────────────────────────────────────────────────────── */}
      {loading ? (
        <p style={{ color: '#94a3b8', fontSize: 13 }}>Loading tasks…</p>
      ) : urgentTasks.length === 0 ? (
        <p style={{ color: '#22c55e', fontSize: 13, textAlign: 'center', padding: '14px 0' }}>
          ✅ All caught up! No urgent tasks.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {urgentTasks.map(task => {
            const pc      = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.Low;
            const overdue = isOverdue(task);
            const today   = isDueToday(task);
            const due     = task.due_date ? new Date(task.due_date) : null;
            return (
              <Link
                key={task._id}
                to={`/tasks/${task._id}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 10px', borderRadius: 7, textDecoration: 'none',
                  border: '1px solid',
                  borderColor: overdue ? '#fecaca' : today ? '#fed7aa' : '#f1f5f9',
                  background:  overdue ? '#fff5f5' : today ? '#fffbeb' : '#fafafa',
                }}
              >
                <span style={{ fontSize: 10, color: '#94a3b8', fontFamily: 'monospace', flexShrink: 0 }}>
                  {task.task_number}
                </span>
                <span style={{
                  fontSize: 13, color: '#1e293b', flex: 1,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {task.title}
                </span>
                <span style={{
                  background: pc.bg, color: pc.text,
                  borderRadius: 4, padding: '1px 6px',
                  fontSize: 10, fontWeight: 600, flexShrink: 0,
                }}>
                  {task.priority}
                </span>
                {due && (
                  <span style={{
                    fontSize: 10, flexShrink: 0,
                    color:      overdue ? '#dc2626' : today ? '#c2410c' : '#94a3b8',
                    fontWeight: overdue || today ? 700 : 400,
                  }}>
                    {due.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      )}

      {/* ── Admin/MD team stats row ──────────────────────────────────────────── */}
      {isAdminOrAbove && teamStats && (
        <div style={{
          marginTop: 14, paddingTop: 12,
          borderTop: '1px solid #f1f5f9',
          display: 'flex', gap: 20, fontSize: 12, color: '#64748b', flexWrap: 'wrap',
        }}>
          <span>
            Team Open:{' '}
            <strong style={{ color: '#1B2A6B' }}>{teamStats.open}</strong>
          </span>
          <span>
            Team Overdue:{' '}
            <strong style={{ color: teamStats.overdue > 0 ? '#dc2626' : '#64748b' }}>
              {teamStats.overdue ?? '…'}
            </strong>
          </span>
        </div>
      )}
    </div>
  );
}
